import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useCart } from '../context/CartContext';

const CartDrawer = () => {
  const { cart, cartTotal, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart, clearCart } = useCart();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);

  const handleCheckout = () => {
    setIsCheckingOut(true);
    setTimeout(() => {
      setIsCheckingOut(false);
      setOrderComplete(true);
      setTimeout(() => {
        setOrderComplete(false);
        setIsCartOpen(false);
        clearCart();
      }, 3000);
    }, 2000);
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsCartOpen(false)}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60]"
            aria-hidden="true"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-[70] shadow-2xl flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cart-title"
          >
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div>
                <h2 id="cart-title" className="text-lg font-bold text-slate-900">Inventory Management</h2>
                <p className="text-[10px] font-mono text-slate-400 uppercase tracking-widest">Active_Session_Buffer</p>
              </div>
              <button 
                onClick={() => setIsCartOpen(false)}
                className="p-2 hover:bg-slate-200 rounded-full transition-colors"
                aria-label="Close cart"
              >
                <SafeIcon icon={FiIcons.FiX} />
              </button>
            </div>

            <div className="flex-grow overflow-y-auto p-6 space-y-6">
              {orderComplete ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiIcons.FiCheck} className="text-3xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Protocol Success</h3>
                    <p className="text-sm text-slate-500">Your workspace components are being initialized.</p>
                  </div>
                </div>
              ) : cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-50">
                  <SafeIcon icon={FiIcons.FiInbox} className="text-5xl text-slate-200" aria-hidden="true" />
                  <div>
                    <p className="font-bold text-slate-400 uppercase text-xs tracking-widest">Buffer_Empty</p>
                    <p className="text-sm text-slate-500">No hardware detected in session.</p>
                  </div>
                </div>
              ) : (
                cart.map((item) => (
                  <div key={item.id} className="flex gap-4 group">
                    <div className="w-20 h-20 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0 border border-slate-200">
                      <picture>
                        <source type="image/avif" srcSet={`${item.image}&fm=avif`} />
                        <source type="image/webp" srcSet={`${item.image}&fm=webp`} />
                        <img 
                          src={item.image} 
                          alt={item.name} 
                          className="w-full h-full object-cover"
                          width="80"
                          height="80"
                          loading="lazy"
                        />
                      </picture>
                    </div>
                    <div className="flex-grow flex flex-col justify-between py-1">
                      <div>
                        <h4 className="text-sm font-bold text-slate-900 line-clamp-1">{item.name}</h4>
                        <p className="text-[10px] font-mono text-slate-400 uppercase">{item.category}</p>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center border border-slate-200 rounded overflow-hidden">
                          <button 
                            onClick={() => updateQuantity(item.id, -1)}
                            className="px-2 py-1 hover:bg-slate-50 text-slate-400"
                            aria-label={`Decrease quantity of ${item.name}`}
                          >
                            <SafeIcon icon={FiIcons.FiMinus} className="text-[10px]" />
                          </button>
                          <span className="px-3 py-1 text-xs font-mono font-bold bg-slate-50 border-x border-slate-200" aria-label="Current quantity">
                            {item.quantity}
                          </span>
                          <button 
                            onClick={() => updateQuantity(item.id, 1)}
                            className="px-2 py-1 hover:bg-slate-50 text-slate-400"
                            aria-label={`Increase quantity of ${item.name}`}
                          >
                            <SafeIcon icon={FiIcons.FiPlus} className="text-[10px]" />
                          </button>
                        </div>
                        <div className="font-mono font-bold text-slate-900">${item.price * item.quantity}</div>
                      </div>
                    </div>
                    <button 
                      onClick={() => removeFromCart(item.id)}
                      className="text-slate-300 hover:text-red-500 transition-colors p-1"
                      aria-label={`Remove ${item.name} from cart`}
                    >
                      <SafeIcon icon={FiIcons.FiTrash2} className="text-sm" />
                    </button>
                  </div>
                ))
              )}
            </div>

            {cart.length > 0 && !orderComplete && (
              <div className="p-6 bg-slate-50 border-t border-slate-200 space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>Subtotal</span>
                    <span className="font-mono">${cartTotal}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>Shipping</span>
                    <span className="font-mono text-emerald-600">PROTOCOL_FREE</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-slate-900 pt-2 border-t border-slate-200">
                    <span>Total Investment</span>
                    <span className="font-mono">${cartTotal}</span>
                  </div>
                </div>
                <button
                  onClick={handleCheckout}
                  disabled={isCheckingOut}
                  className={`w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-lg transition-all flex items-center justify-center gap-3 ${isCheckingOut ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isCheckingOut ? (
                    <>
                      <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" />
                      Initializing_Protocol...
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiIcons.FiZap} />
                      Complete Checkout
                    </>
                  )}
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;